import ListViewModel from "../../../../listViewModel";
import type ChargeResponse from "entities/chargeResponse";
import LocalizationService from "services/localizationService";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import ChargesRepository from "repositories/chargesRepository";
import type { IResults } from "../../../../../data/types";
import { bound } from "@frui.ts/helpers";
import type { interfaces } from "inversify";
import { Modules } from "../../../../../config";
import type ChargeRefundResponse from "entities/chargeRefundResponse";
import RefundDisplayStatus from "entities/refundDisplayStatus";
import type RefundDisplayType from "entities/refundDisplayType";
import type CustomerRefundPaymentType from "entities/customerRefundPaymentType";

export default class ChargeRefundsViewModel extends ListViewModel<ChargeRefundResponse> {
  readonly modelName = "chargeRefund";

  constructor(
    public charge: ChargeResponse,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: ChargesRepository
  ) {
    super();
    this.navigationName = "";
  }

  protected async fetchData(): Promise<IResults<ChargeRefundResponse>> {
    return this.repository.getRefunds(this.charge.chargeId);
  }

  @bound
  onClose() {
    this.requestClose();
  }

  approvedOrRejectedAt(item: ChargeRefundResponse) {
    if (item.refundDisplayStatus == RefundDisplayStatus.Rejected) {
      return item.rejectedAt;
    } else {
      return item.approvedAt;
    }
  }

  approvedOrRejectedBy(item: ChargeRefundResponse) {
    if (item.refundDisplayStatus == RefundDisplayStatus.Rejected) {
      return item.rejectedBy;
    } else {
      return item.approvedBy;
    }
  }

  refundPaymentType(refundPaymentType?: CustomerRefundPaymentType) {
    if (!refundPaymentType) {
      return "";
    }

    return this.localizationService.translateEnum("customerRefundPaymentType", refundPaymentType);
  }

  refundPaymentDate(item: ChargeRefundResponse) {
    return item.refundPayGatePayment?.authorizedAt ?? item.refundTransferPayment?.postingDate;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `chargeRefunds.${code}`, size);
  }

  translateChargeRefundDisplayStatus(refundDisplayStatus: RefundDisplayStatus) {
    return this.localizationService.translateEnum("refundDisplayStatus", refundDisplayStatus);
  }

  translateChargeRefundDisplayType(refundDisplayType: RefundDisplayType) {
    return this.localizationService.translateEnum("refundDisplayType", refundDisplayType);
  }

  static Factory({ container }: interfaces.Context) {
    return (charge: ChargeResponse) =>
      new ChargeRefundsViewModel(
        charge,
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(ChargesRepository)
      );
  }
}
